import { useState, useEffect, useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Clock,
  Ban,
  User,
  Smartphone,
  CreditCard,
  DollarSign,
  FileText,
  Pencil,
  Send,
  AlertTriangle,
  Trash2,
} from '@/lib/icons';

import {
  getApplicationById,
  verifyApplicationKyc,
  approveApplication,
  rejectApplication,
  cancelApplication,
  pushApplicationPayment,
  deleteApplication,
} from '@/features/applications/api';
import type {
  ApplicationStatusType,
  ApplicationPaymentT,
  ApplicationPaymentTypeT,
} from '@/features/applications/application';
import { installmentSummary } from '@/features/applications/application';
import { CustomerEligibilityAssessment } from '@/features/eligibility';
import { PaymentRailPicker } from '@/features/payments/PaymentRailPicker';
import {
  DEFAULT_PAYMENT_RAIL,
  type PaymentRailType,
} from '@/features/payments/payment';
import type { KycStatusType } from '@/features/customers/customer';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/store/hooks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { formatPhoneDisplay, parsePhone } from '@/lib/phone';
import { cn, formatCurrency } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { hasMarkup } from '@/lib/markup';
import { ActionLoadingOverlay, ButtonSpinner } from '@/components/feedback';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { formatDate, formatDateTime } from '@/lib/dates';

export const Route = createFileRoute('/_layout/applications/$applicationId')({
  component: ApplicationDetailPage,
});

// One colour per status, consumed by both the badge and the stepper's current
// step — so the two can never disagree about the same state.
type StatusConfig = {
  label: string;
  className: string;
  dot: string;
  text: string;
  icon: React.ReactNode;
};

const STATUS_CONFIG: Record<ApplicationStatusType, StatusConfig> = {
  draft: {
    label: 'Draft',
    className:
      'bg-slate-500/15 text-slate-600 border-slate-500/30 dark:text-slate-400',
    dot: 'border-slate-500 bg-slate-500/10',
    text: 'text-slate-600 dark:text-slate-400',
    icon: <Pencil className="w-3 h-3 mr-1" />,
  },
  submitted: {
    label: 'Submitted',
    className:
      'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    dot: 'border-blue-500 bg-blue-500/10',
    text: 'text-blue-600 dark:text-blue-400',
    icon: <Clock className="w-3 h-3 mr-1" />,
  },
  pending_kyc: {
    label: 'Pending KYC',
    className:
      'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
    dot: 'border-amber-500 bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
    icon: <ShieldCheck className="w-3 h-3 mr-1" />,
  },
  approved: {
    label: 'Approved',
    className:
      'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
    dot: 'border-emerald-500 bg-emerald-500/10',
    text: 'text-emerald-600 dark:text-emerald-400',
    icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
    dot: 'border-red-500 bg-red-500/10',
    text: 'text-red-600 dark:text-red-400',
    icon: <XCircle className="w-3 h-3 mr-1" />,
  },
  cancelled: {
    label: 'Cancelled',
    className:
      'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
    dot: 'border-gray-500 bg-gray-500/10',
    text: 'text-gray-600 dark:text-gray-400',
    icon: <Ban className="w-3 h-3 mr-1" />,
  },
};

// ── Lifecycle phases ────────────────────────────────────────────────────────
// `status` alone can't describe the lifecycle: `pending_kyc` covers both
// "deposit paid, awaiting KYC" and "KYC verified, awaiting approval", and
// `submitted` is a legacy default no flow assigns. The real phase comes from
// status + customerKycStatus + depositPaidAt.

type PhaseState = 'complete' | 'current' | 'pending' | 'failed';

const PHASES = [
  { key: 'submitted', label: 'Submitted', icon: <FileText className="h-4 w-4" /> },
  { key: 'deposit', label: 'Deposit Paid', icon: <CreditCard className="h-4 w-4" /> },
  { key: 'kyc', label: 'KYC Verified', icon: <ShieldCheck className="h-4 w-4" /> },
  { key: 'decision', label: 'Approved', icon: <CheckCircle2 className="h-4 w-4" /> },
] as const;

function phaseProgress(
  status: ApplicationStatusType,
  kycVerified: boolean,
  depositPaid: boolean
): number {
  if (status === 'approved') return PHASES.length;
  if (depositPaid && kycVerified) return 3;
  if (depositPaid) return 2;
  // Terminal without a deposit still cleared the "Submitted" milestone.
  return status === 'rejected' || status === 'cancelled' ? 1 : 0;
}

function phaseState(
  index: number,
  progress: number,
  terminal: boolean
): PhaseState {
  if (terminal && index === PHASES.length - 1) return 'failed';
  if (index < progress) return 'complete';
  if (index === progress && !terminal) return 'current';
  return 'pending';
}

function getStatusBadge(status: string) {
  const cfg = STATUS_CONFIG[status as ApplicationStatusType] ?? {
    label: status,
    className:
      'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
    icon: <FileText className="w-3 h-3 mr-1" />,
  };
  return (
    <Badge className={cn('text-sm', cfg.className)}>
      {cfg.icon}
      {cfg.label}
    </Badge>
  );
}

const tierColors: Record<string, string> = {
  A: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/30',
  B: 'text-blue-600 bg-blue-500/10 border-blue-500/30',
  C: 'text-amber-600 bg-amber-500/10 border-amber-500/30',
  D: 'text-orange-600 bg-orange-500/10 border-orange-500/30',
  E: 'text-red-600 bg-red-500/10 border-red-500/30',
};

function ApplicationDetailPage() {
  const { applicationId } = Route.useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [kycSuccess, setKycSuccess] = useState(false);
  const [approveSuccess, setApproveSuccess] = useState(false);
  const [rejectSuccess, setRejectSuccess] = useState(false);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushPayerOverride, setPushPayerOverride] = useState('');
  /** Empty means "push to the customer on file"; partial means "not yet". */
  const pushPayer = useMemo(
    () => parsePhone(pushPayerOverride),
    [pushPayerOverride]
  );
  const [pushPaymentMethod, setPushPaymentMethod] =
    useState<PaymentRailType>(DEFAULT_PAYMENT_RAIL);
  // The dialog's payment-type is implied by current state (deposit while
  // status=draft, excess while status=approved with excess owed). We
  // capture it on open so that a refetch mid-flow doesn't shift it.
  const [pushPaymentType, setPushPaymentType] =
    useState<ApplicationPaymentTypeT>('down_payment');
  // Stores the contractId returned by the approve mutation so we can
  // navigate to the new contract once the success dialog closes. The
  // GET-application query DOES NOT reliably return this field — the
  // mutation response is the authoritative source.
  const [approvedContractId, setApprovedContractId] = useState<
    string | null
  >(null);
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  const { data, isLoading, error } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => getApplicationById(applicationId),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });

  const app = data?.value;
  const schedule = app ? installmentSummary(app) : null;

  const kycMutation = useMutation({
    mutationFn: () => verifyApplicationKyc(applicationId),
    onSuccess: (result) => {
      if (result.succeeded) {
        setKycSuccess(true);
        queryClient.invalidateQueries({
          queryKey: ['application', applicationId],
        });
        queryClient.invalidateQueries({ queryKey: ['applications'] });
        queryClient.invalidateQueries({ queryKey: ['applications-counts'] });
      } else {
        // Reachable only if the API wrapper ever stops throwing on
        // succeeded:false. The onError branch below handles the real path.
        toast({
          title: 'Error',
          description: result.errors?.[0] ?? 'Failed to verify KYC',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      // handleApiResponse throws ApiError on succeeded:false, so failures
      // arrive here, not in onSuccess. Close the dialog and surface the
      // backend's error message so the user can act on it.
      setKycDialogOpen(false);
      setKycSuccess(false);
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to verify KYC',
        variant: 'destructive',
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => approveApplication(applicationId),
    onSuccess: (result) => {
      if (result.succeeded) {
        // Capture contractId directly from the mutation response. The
        // backend returns `ApplicationApproveResponse { application, contractId }`
        // at result.value — this is the only authoritative source.
        const newContractId = result.value?.contractId ?? null;
        setApprovedContractId(newContractId);
        setApproveSuccess(true);
        queryClient.invalidateQueries({
          queryKey: ['application', applicationId],
        });
        queryClient.invalidateQueries({ queryKey: ['applications'] });
        queryClient.invalidateQueries({ queryKey: ['applications-counts'] });
        queryClient.invalidateQueries({ queryKey: ['contracts'] });

        if (!newContractId) {
          // Defensive: log + toast so we notice if the backend ever stops
          // returning contractId. User will stay on this page.
          console.error('approve_application_missing_contract_id', result);
          toast({
            title: 'Approved',
            description:
              'Application approved but the linked contract ID was missing — please refresh.',
            variant: 'destructive',
          });
        }
      } else {
        // Reachable only if the API wrapper ever stops throwing on
        // succeeded:false. The onError branch below handles the real path.
        toast({
          title: 'Error',
          description: result.errors?.[0] ?? 'Failed to approve application',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      // handleApiResponse throws ApiError on succeeded:false. Surface the
      // backend's reason via a toast and leave the dialog open so the user
      // can correct the issue and retry without re-opening it.
      toast({
        title: 'Approval failed',
        description:
          error instanceof Error ? error.message : 'Failed to approve application',
        variant: 'destructive',
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => rejectApplication(applicationId, reason),
    onSuccess: (result) => {
      if (result.succeeded) {
        setRejectSuccess(true);
        queryClient.invalidateQueries({
          queryKey: ['application', applicationId],
        });
        queryClient.invalidateQueries({ queryKey: ['applications'] });
        queryClient.invalidateQueries({ queryKey: ['applications-counts'] });
      } else {
        toast({
          title: 'Error',
          description: result.errors?.[0] ?? 'Failed to reject application',
          variant: 'destructive',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteApplication(applicationId),
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['applications'] });
        queryClient.invalidateQueries({
          queryKey: ['applications-counts'],
        });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        toast({
          title: 'Application Deleted',
          description: 'The application has been permanently removed.',
        });
        navigate({ to: '/applications' });
      } else {
        toast({
          title: 'Error',
          description:
            result.errors?.[0] ?? 'Failed to delete application',
          variant: 'destructive',
        });
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelApplication(applicationId),
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
        queryClient.invalidateQueries({ queryKey: ['applications'] });
        queryClient.invalidateQueries({ queryKey: ['applications-counts'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        toast({
          title: 'Application Cancelled',
          description: 'The device has been released back to stock.',
        });
      } else {
        toast({
          title: 'Error',
          description: result.errors?.[0] ?? 'Failed to cancel application',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      // handleApiResponse throws on succeeded:false, so a refusal lands here.
      // Refetch so the page reflects whatever closed the window (e.g. a deposit
      // that settled meanwhile) and the Cancel action disappears with it.
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
      toast({
        title: 'Could not cancel application',
        description:
          error instanceof Error ? error.message : 'Failed to cancel application',
        variant: 'destructive',
      });
    },
  });

  const pushPaymentMutation = useMutation({
    mutationFn: (payload: {
      paymentType: ApplicationPaymentTypeT;
      payerMsisdn?: string;
      paymentMethod: string;
    }) => pushApplicationPayment(applicationId, payload),
    onSuccess: (result) => {
      if (result.succeeded) {
        setPushDialogOpen(false);
        setPushPayerOverride('');
        queryClient.invalidateQueries({
          queryKey: ['application', applicationId],
        });
        queryClient.invalidateQueries({ queryKey: ['applications'] });
        queryClient.invalidateQueries({ queryKey: ['applications-counts'] });
        toast({
          title:
            result.value?.gatewayStatus === 'success'
              ? '✅ Payment received'
              : '📲 Payment prompt sent',
          description: result.value?.message ?? 'Payment initiated.',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Error',
          description: result.errors?.[0] ?? 'Failed to push payment',
          variant: 'destructive',
        });
      }
    },
    // handleApiResponse throws an ApiError when the backend returns
    // succeeded:false (or a non-2xx). Without this handler the rejection was
    // swallowed — the button span indefinitely and no toast appeared. Surface
    // the real reason (e.g. status gate, missing phone, gateway error).
    onError: (error: unknown) => {
      const e = error as { errors?: string[]; message?: string };
      toast({
        title: 'Payment failed',
        description: e?.errors?.[0] ?? e?.message ?? 'Failed to push payment. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Auto-close KYC dialog after success
  useEffect(() => {
    if (kycSuccess) {
      const timer = setTimeout(() => {
        setKycDialogOpen(false);
        setKycSuccess(false);
        toast({
          title: '✅ KYC Verified',
          description: `${app?.customerName ?? 'Customer'}'s identity has been verified. The application is ready for approval.`,
          duration: 5000,
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [kycSuccess, toast, app?.customerName]);

  // Auto-close Approve dialog after success and navigate to the new contract.
  // The contract UUID comes from the approve mutation response (stored in
  // `approvedContractId`), NOT from the GET-application query data.
  useEffect(() => {
    if (!approveSuccess) return;

    const timer = setTimeout(() => {
      setApproveDialogOpen(false);
      setApproveSuccess(false);
      const contractId = approvedContractId ?? app?.contractId ?? null;
      toast({
        title: '🎉 Application Approved!',
        description: contractId
          ? `Contract created for ${app?.customerName ?? 'Customer'}. Redirecting to contract activation...`
          : `Application approved for ${app?.customerName ?? 'Customer'}.`,
        duration: 5000,
      });
      if (contractId) {
        setTimeout(() => {
          navigate({
            to: '/contracts/$contractId',
            params: { contractId },
          });
        }, 500);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [
    approveSuccess,
    toast,
    app?.customerName,
    app?.contractId,
    approvedContractId,
    navigate,
  ]);

  // Auto-close Reject dialog after success
  useEffect(() => {
    if (rejectSuccess) {
      const timer = setTimeout(() => {
        setRejectDialogOpen(false);
        setRejectSuccess(false);
        setRejectReason('');
        toast({
          title: 'Application Rejected',
          description: 'The application has been rejected and recorded.',
          duration: 5000,
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [rejectSuccess, toast]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Application not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/applications' })}
        >
          Back to Applications
        </Button>
      </div>
    );
  }

  // Real KYC state lives on the customer, not the application. `pending_kyc`
  // means neither KYC verified nor deposit paid — deposit state is derived
  // from the payments collection below, not from the status.
  const isKycVerified = app.customerKycStatus === 'verified';

  // Status decides whether the action is legal, the grant whether this
  // operator may take it. Both are required.
  const canVerifyKyc =
    !isKycVerified &&
    (app.status === 'submitted' || app.status === 'pending_kyc') &&
    hasPermission('applications', 'verifyKyc');
  const canApprove =
    app.status === 'pending_kyc' &&
    isKycVerified &&
    hasPermission('applications', 'approve');
  const canReject =
    (app.status === 'submitted' || app.status === 'pending_kyc') &&
    hasPermission('applications', 'reject');
  const canDelete =
    (app.status === 'draft' || app.status === 'cancelled') &&
    hasPermission('applications', 'delete');
  // Frees the reserved handset. Mirrors the API's refusal (a settled deposit's
  // exit is a refund), so the button never offers a cancel that must fail.
  // An in-flight payment does not block a cancel.
  const cancelLocked =
    app.lockReason === 'DEPOSIT_SETTLED' ||
    app.lockReason === 'UNDER_REVIEW' ||
    app.lockReason === 'ALREADY_DECIDED';
  const canCancel =
    (app.status === 'draft' ||
      app.status === 'submitted' ||
      app.status === 'pending_kyc') &&
    !cancelLocked &&
    hasPermission('applications', 'cancel');

  // Payment-derived state. `outstandingType` is the kind of payment that
  // can next be pushed (deposit pre-KYC, excess post-approval); null when
  // there's nothing actionable. `outstandingAmount` drives the dialog's
  // headline number and the button label.
  const payments: ApplicationPaymentT[] = app.payments ?? [];
  const completedDeposit = payments.some(
    (p) => p.type === 'down_payment' && p.status === 'completed'
  );
  const completedExcess = payments.some(
    (p) => p.type === 'excess' && p.status === 'completed'
  );
  const pendingDeposit = payments.some(
    (p) => p.type === 'down_payment' && p.status === 'pending'
  );
  const pendingExcess = payments.some(
    (p) => p.type === 'excess' && p.status === 'pending'
  );

  const statusCfg = STATUS_CONFIG[app.status] ?? STATUS_CONFIG.draft;
  const isTerminal = app.status === 'rejected' || app.status === 'cancelled';
  // A completed deposit row is the direct signal; the status transition it
  // triggers is the fallback for rows predating the payments collection.
  const depositPaid =
    completedDeposit ||
    app.status === 'pending_kyc' ||
    app.status === 'approved';
  const progress = phaseProgress(app.status, isKycVerified, depositPaid);

  let outstandingType: ApplicationPaymentTypeT | null = null;
  let outstandingAmount = 0;
  if (
    // Deposit is outstanding in any pre-approval state, incl. pending_kyc.
    (app.status === 'draft' ||
      app.status === 'submitted' ||
      app.status === 'pending_kyc') &&
    !completedDeposit
  ) {
    outstandingType = 'down_payment';
    outstandingAmount = app.downPaymentAmount;
  } else if (
    // Excess is part of the upfront and must be collected before approval
    // (the new approve flow refuses to proceed when the upfront is short).
    // Surface the push action whenever the deposit has landed and there's
    // still excess outstanding — covering pending_kyc/submitted/approved.
    (app.status === 'submitted' ||
      app.status === 'pending_kyc' ||
      app.status === 'approved') &&
    app.upfrontExcess > 0 &&
    !completedExcess
  ) {
    outstandingType = 'excess';
    outstandingAmount = app.upfrontExcess;
  }
  const outstandingPending =
    (outstandingType === 'down_payment' && pendingDeposit) ||
    (outstandingType === 'excess' && pendingExcess);
  // Backend gates push-payment on applications:update.
  const canPushPayment =
    outstandingType !== null && hasPermission('applications', 'update');

  const openPushDialog = () => {
    if (!outstandingType) return;
    setPushPaymentType(outstandingType);
    setPushPayerOverride('');
    setPushPaymentMethod(DEFAULT_PAYMENT_RAIL);
    setPushDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/applications' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">
            {app.customerName ?? 'Application'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {app.customerPhone ?? ''} &middot;{' '}
            {app.deviceBrand
              ? `${app.deviceBrand} ${app.deviceModel ?? ''}`
              : 'Device'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(app.status)}

          {canCancel && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Ban className="mr-1 h-3.5 w-3.5" />
                  Cancel
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <ActionLoadingOverlay
                  loading={cancelMutation.isPending}
                  loadingTitle="Cancelling Application..."
                  loadingSubtitle="Withdrawing the application and releasing its device"
                  steps={[
                    { label: 'Withdrawing application', status: 'active' },
                    { label: 'Releasing device to stock', status: 'pending' },
                  ]}
                />
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Application</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <span className="block">
                      Withdraw the{' '}
                      <span className="font-semibold">{app.status}</span>{' '}
                      application for{' '}
                      <span className="font-semibold">
                        {app.customerName}
                      </span>
                      ? The reserved device returns to stock and can be sold
                      to someone else.
                    </span>
                    <span className="block">
                      The application stays on record, and any payments keep
                      their current status — cancelling does not refund or
                      reverse money.
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={cancelMutation.isPending}>
                    Keep Application
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.preventDefault();
                      cancelMutation.mutate();
                    }}
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Application'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {canPushPayment && (
            <Button
              size="sm"
              variant="outline"
              className="border-primary/40 text-primary hover:bg-primary/10"
              onClick={openPushDialog}
              disabled={outstandingPending || pushPaymentMutation.isPending}
            >
              <Send className="mr-1 h-3.5 w-3.5" />
              {outstandingPending
                ? 'Awaiting payment…'
                : `Push ${
                    outstandingType === 'down_payment' ? 'Deposit' : 'Excess'
                  } ${formatCurrency(outstandingAmount, currency)}`}
            </Button>
          )}

          {canVerifyKyc && (
            <AlertDialog
              open={kycDialogOpen}
              onOpenChange={(open) => {
                if (!open && (kycMutation.isPending || kycSuccess)) return;
                setKycDialogOpen(open);
              }}
            >
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-warning text-warning-foreground hover:bg-warning/90"
                >
                  <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                  Verify KYC
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <ActionLoadingOverlay
                  loading={kycMutation.isPending}
                  success={kycSuccess}
                  loadingTitle="Verifying KYC..."
                  loadingSubtitle="Checking and updating customer identity verification"
                  successTitle="KYC Verified!"
                  successSubtitle="Customer identity confirmed successfully"
                  steps={[
                    { label: 'Verifying identity documents', status: 'active' },
                    { label: 'Updating customer record', status: 'pending' },
                    { label: 'Advancing application', status: 'pending' },
                  ]}
                />
                <AlertDialogHeader>
                  <AlertDialogTitle>Verify KYC</AlertDialogTitle>
                  <AlertDialogDescription>
                    Mark the customer KYC as verified for{' '}
                    <span className="font-semibold">{app.customerName}</span>?
                    This will update the customer record and advance the
                    application for approval.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={kycMutation.isPending || kycSuccess}
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={(e) => {
                      e.preventDefault();
                      kycMutation.mutate();
                    }}
                    disabled={kycMutation.isPending || kycSuccess}
                  >
                    {kycMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Verifying...
                      </>
                    ) : (
                      'Verify KYC'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {canApprove && (
            <AlertDialog
              open={approveDialogOpen}
              onOpenChange={(open) => {
                if (!open && (approveMutation.isPending || approveSuccess))
                  return;
                setApproveDialogOpen(open);
              }}
            >
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-success text-success-foreground hover:bg-success/90"
                >
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                  Approve
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="sm:max-w-lg">
                <ActionLoadingOverlay
                  loading={approveMutation.isPending}
                  success={approveSuccess}
                  loadingTitle="Approving Application..."
                  loadingSubtitle="Creating contract and setting up financing terms"
                  successTitle="Application Approved!"
                  successSubtitle="Contract created successfully"
                  steps={[
                    { label: 'Validating application data', status: 'active' },
                    { label: 'Creating financing contract', status: 'pending' },
                    { label: 'Reserving device unit', status: 'pending' },
                  ]}
                />
                <AlertDialogHeader>
                  <AlertDialogTitle>Approve Application</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-3">
                      <p>
                        Approve the financing application for{' '}
                        <span className="font-semibold">
                          {app.customerName}
                        </span>
                        ? This will create a new contract with the following
                        terms:
                      </p>
                      <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Device</span>
                          <span className="font-medium">
                            {app.deviceBrand} {app.deviceModel}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Device Price
                          </span>
                          <span>
                            {formatCurrency(app.devicePrice, currency)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Credit Limit (Tier {app.creditTier ?? '?'})
                          </span>
                          <span>
                            {app.creditLimit != null
                              ? formatCurrency(app.creditLimit, currency)
                              : '-'}
                          </span>
                        </div>
                        {app.upfrontExcess > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Upfront Excess
                            </span>
                            <span className="text-amber-600 dark:text-amber-400">
                              {formatCurrency(app.upfrontExcess, currency)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Down Payment
                          </span>
                          <span>
                            {formatCurrency(app.downPaymentAmount, currency)}
                          </span>
                        </div>
                        <div className="flex justify-between border-t pt-1.5">
                          <span className="text-muted-foreground font-medium">
                            Total Upfront
                          </span>
                          <span className="font-bold">
                            {formatCurrency(app.totalUpfront, currency)}
                          </span>
                        </div>
                        {hasMarkup(app.markupAmount) && (
                          <div className="flex justify-between border-t pt-1.5">
                            <span className="text-muted-foreground">
                              Murabaha Markup
                            </span>
                            <span>
                              {formatCurrency(app.markupAmount, currency)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Financed Amount
                          </span>
                          <span className="font-semibold">
                            {formatCurrency(app.financedAmount, currency)}
                          </span>
                        </div>
                        {schedule ? (
                          <>
                            <div className="flex justify-between border-t pt-1.5">
                              <span className="text-muted-foreground font-medium">
                                {schedule.label}
                              </span>
                              <span className="font-bold text-base">
                                {formatCurrency(schedule.amount, currency)} ×{' '}
                                {schedule.count}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Term</span>
                              <span>{app.termMonths} months</span>
                            </div>
                          </>
                        ) : (
                          <div className="border-t pt-1.5 text-amber-600 dark:text-amber-400">
                            This plan has no term configured, so no repayment
                            schedule can be generated. Approval will fail until
                            the plan is fixed.
                          </div>
                        )}
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={approveMutation.isPending || approveSuccess}
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    onClick={(e) => {
                      e.preventDefault();
                      approveMutation.mutate();
                    }}
                    disabled={approveMutation.isPending || approveSuccess}
                  >
                    {approveMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Approving...
                      </>
                    ) : (
                      'Approve & Create Contract'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {canReject && (
            <Dialog
              open={rejectDialogOpen}
              onOpenChange={(open) => {
                if (!open && (rejectMutation.isPending || rejectSuccess))
                  return;
                setRejectDialogOpen(open);
              }}
            >
              <DialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <XCircle className="mr-1 h-3.5 w-3.5" />
                  Reject
                </Button>
              </DialogTrigger>
              <DialogContent>
                <ActionLoadingOverlay
                  loading={rejectMutation.isPending}
                  success={rejectSuccess}
                  loadingTitle="Rejecting Application..."
                  loadingSubtitle="Recording rejection and updating application status"
                  successTitle="Application Rejected"
                  successSubtitle="The rejection has been recorded"
                  tone="destructive"
                  steps={[
                    { label: 'Recording rejection reason', status: 'active' },
                    { label: 'Updating application status', status: 'pending' },
                  ]}
                />
                <DialogHeader>
                  <DialogTitle>Reject Application</DialogTitle>
                  <DialogDescription>
                    Provide a reason for rejecting{' '}
                    <span className="font-semibold">{app.customerName}</span>
                    's application. This will be recorded and visible to staff.
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  placeholder="Enter rejection reason..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  disabled={rejectMutation.isPending || rejectSuccess}
                />
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setRejectDialogOpen(false)}
                    disabled={rejectMutation.isPending || rejectSuccess}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => rejectMutation.mutate(rejectReason)}
                    disabled={
                      rejectMutation.isPending ||
                      rejectSuccess ||
                      rejectReason.trim() === ''
                    }
                  >
                    {rejectMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Rejecting...
                      </>
                    ) : (
                      'Reject Application'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <ActionLoadingOverlay
                  loading={deleteMutation.isPending}
                  loadingTitle="Deleting Application..."
                  loadingSubtitle="Permanently removing the application and releasing any reserved device"
                  tone="destructive"
                  steps={[
                    {
                      label: 'Removing application data',
                      status: 'active',
                    },
                    {
                      label: 'Releasing reserved device',
                      status: 'pending',
                    },
                  ]}
                />
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-destructive">
                    Delete Application Permanently
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <span className="block">
                      Are you sure you want to delete the{' '}
                      <span className="font-semibold">{app.status}</span>{' '}
                      application for{' '}
                      <span className="font-semibold">
                        {app.customerName}
                      </span>
                      ?
                    </span>
                    <span className="block text-destructive font-medium">
                      This will permanently remove the application from
                      the system and return any reserved device to
                      inventory. This action cannot be undone.
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={deleteMutation.isPending}
                  >
                    Keep Application
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteMutation.mutate();
                    }}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Deleting...
                      </>
                    ) : (
                      'Delete Permanently'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {canPushPayment && (
            <Dialog
              open={pushDialogOpen}
              onOpenChange={(open) => {
                if (!open && pushPaymentMutation.isPending) return;
                setPushDialogOpen(open);
              }}
            >
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    Push {pushPaymentType === 'down_payment' ? 'Deposit' : 'Excess'}{' '}
                    Payment
                  </DialogTitle>
                  <DialogDescription>
                    Send a {formatCurrency(outstandingAmount, currency)}{' '}
                    {pushPaymentType === 'down_payment'
                      ? 'earnest deposit'
                      : 'upfront-excess'}{' '}
                    prompt to the customer's phone — or to a different payer's
                    phone for pay-by-another-person.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="payerMsisdn">Payer phone number</Label>
                    <PhoneInput
                      id="payerMsisdn"
                      value={pushPayerOverride}
                      onChange={setPushPayerOverride}
                      disabled={pushPaymentMutation.isPending}
                      aria-describedby="payerMsisdn-hint"
                    />
                    <p id="payerMsisdn-hint" className="text-xs text-muted-foreground">
                      Leave empty to push to{' '}
                      <span className="font-medium">
                        {app.customerName ?? 'customer'}
                      </span>{' '}
                      (
                      {app.customerPhone
                        ? formatPhoneDisplay(app.customerPhone)
                        : 'no phone on file'}
                      ).
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Payment method</Label>
                    <PaymentRailPicker
                      value={pushPaymentMethod}
                      onChange={setPushPaymentMethod}
                      disabled={pushPaymentMutation.isPending}
                      hint="The payer approves the prompt on their phone — the payment lands here once they confirm."
                    />
                  </div>

                  <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type</span>
                      {/* The wire enum, not a label — `capitalize` on it read
                          as "Down_payment". Use the same wording the dialog
                          title already uses. */}
                      <span className="font-medium">
                        {pushPaymentType === 'down_payment'
                          ? 'Deposit'
                          : 'Excess'}
                      </span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-bold">
                        {formatCurrency(outstandingAmount, currency)}
                      </span>
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setPushDialogOpen(false)}
                    disabled={pushPaymentMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() =>
                      pushPaymentMutation.mutate({
                        paymentType: pushPaymentType,
                        payerMsisdn: pushPayer.e164 ?? undefined,
                        paymentMethod: pushPaymentMethod,
                      })
                    }
                    // An override that is half-typed would send the prompt to a
                    // number nobody answers, with the payer standing right
                    // there. Empty is fine — that is "push to the customer".
                    disabled={pushPaymentMutation.isPending || pushPayer.isPartial}
                  >
                    {pushPaymentMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Prompt
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* ── Outstanding Upfront Banner ─────────────────────────── */}
      {canPushPayment && (
        <div className="rounded-lg border border-amber-300/70 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/30 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-amber-800 dark:text-amber-300">
                {outstandingType === 'down_payment'
                  ? 'Earnest deposit not yet collected'
                  : 'Upfront excess not yet collected'}
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                {formatCurrency(outstandingAmount, currency)} is owed before
                {outstandingType === 'down_payment'
                  ? ' KYC review can begin.'
                  : ' the contract can be activated.'}{' '}
                Push the payment prompt to the customer or another payer.
              </p>
            </div>
            <Button
              size="sm"
              onClick={openPushDialog}
              disabled={outstandingPending || pushPaymentMutation.isPending}
              className="shrink-0"
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              {outstandingPending ? 'Awaiting…' : 'Push Payment'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Status Stepper ────────────────────────────────────── */}
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center justify-between">
          {PHASES.map((phase, idx, arr) => {
            const isLast = idx === arr.length - 1;
            const state = phaseState(idx, progress, isTerminal);
            const active = state === 'current' || state === 'failed';
            return (
              <div key={phase.key} className="flex flex-1 items-center">
                <div
                  className={cn(
                    'flex items-center gap-2',
                    state === 'complete' &&
                      'text-emerald-600 dark:text-emerald-400',
                    active && statusCfg.text,
                    state === 'pending' && 'text-muted-foreground/50'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors',
                      state === 'complete' &&
                        'border-emerald-500 bg-emerald-500/10',
                      active && statusCfg.dot,
                      state === 'pending' && 'border-border'
                    )}
                  >
                    {isLast && isTerminal ? (
                      app.status === 'rejected' ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <Ban className="h-4 w-4" />
                      )
                    ) : (
                      phase.icon
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium hidden sm:inline',
                      state !== 'pending' && 'font-semibold'
                    )}
                  >
                    {isLast && isTerminal ? statusCfg.label : phase.label}
                  </span>
                </div>
                {!isLast && (
                  <div
                    className={cn(
                      'mx-2 h-0.5 flex-1 rounded-full',
                      state === 'complete' ? 'bg-emerald-500' : 'bg-border'
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Rejection reason banner */}
      {app.status === 'rejected' && app.rejectionReason && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-4">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-red-800 dark:text-red-300">
                Rejection Reason
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                {app.rejectionReason}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Approved — link to contract */}
      {app.status === 'approved' && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-emerald-800 dark:text-emerald-300">
                  Application Approved
                </p>
                <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
                  A contract has been created. The next step is to activate it
                  at the branch.
                </p>
              </div>
            </div>
            {app.contractId && (
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/30 shrink-0 ml-4"
                onClick={() =>
                  navigate({
                    to: '/contracts/$contractId',
                    params: { contractId: app.contractId! },
                  })
                }
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" />
                View Contract
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Main layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Information
                </CardTitle>
                {app.customerId && (
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: app.customerId }}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background rounded-sm"
                  >
                    View customer
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <InfoRow label="Name" value={app.customerName ?? '-'} />
                <InfoRow label="Phone" value={app.customerPhone ?? '-'} />
                <InfoRow label="Branch" value={app.branchName ?? '-'} />
                <InfoRow
                  label="Submitted"
                  value={formatDate(app.createdAt)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Device Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Device Information
                </CardTitle>
                {app.deviceId && (
                  <Link
                    to="/inventory/units/$unitId"
                    params={{ unitId: app.deviceId }}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background rounded-sm"
                  >
                    View device
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <InfoRow label="Brand" value={app.deviceBrand ?? '-'} />
                <InfoRow label="Model" value={app.deviceModel ?? '-'} />
                <InfoRow
                  label="Device Price"
                  value={formatCurrency(app.devicePrice, currency)}
                />
                <InfoRow label="Financing Plan" value={app.planName ?? '-'} />
              </div>
            </CardContent>
          </Card>

          {/* Credit Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Credit Assessment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <InfoRow
                  label="Credit Score"
                  value={
                    app.creditScore != null ? String(app.creditScore) : '-'
                  }
                />
                <InfoRow
                  label="Credit Tier"
                  value={
                    app.creditTier ? (
                      <span
                        className={cn(
                          'inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-bold',
                          tierColors[app.creditTier] ?? ''
                        )}
                      >
                        {app.creditTier}
                      </span>
                    ) : (
                      '-'
                    )
                  }
                />
                <InfoRow
                  label="Credit Limit"
                  value={
                    app.creditLimit != null
                      ? formatCurrency(app.creditLimit, currency)
                      : '-'
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Eligibility Assessment — full 26-check scorecard for the
              applicant, with this application's deposit + installment context
              so affordability and deposit validation reflect the real terms. */}
          <CustomerEligibilityAssessment
            msisdn={app.customerPhone}
            kycStatus={(app.customerKycStatus as KycStatusType | null) ?? null}
            creditScore={app.creditScore}
            creditTier={app.creditTier}
            monthlyInstallment={app.monthlyInstallment}
            deposit={{
              required: app.downPaymentAmount > 0,
              paid: completedDeposit,
              pending: pendingDeposit,
            }}
          />
        </div>

        {/* Right column -- Pricing Breakdown */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Pricing Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow
                label="Device Price"
                value={formatCurrency(app.devicePrice, currency)}
              />
              <InfoRow
                label={`Credit Limit${app.creditTier ? ` (Tier ${app.creditTier})` : ''}`}
                value={
                  app.creditLimit != null
                    ? formatCurrency(app.creditLimit, currency)
                    : '-'
                }
              />
              {app.upfrontExcess > 0 && (
                <InfoRow
                  label="Upfront Excess"
                  value={
                    <span className="text-amber-600 dark:text-amber-400">
                      {formatCurrency(app.upfrontExcess, currency)}
                    </span>
                  }
                />
              )}
              <InfoRow
                label="Down Payment"
                value={formatCurrency(app.downPaymentAmount, currency)}
              />
              <div className="border-t pt-3">
                <InfoRow
                  label="Total Upfront"
                  value={
                    <span className="font-bold">
                      {formatCurrency(app.totalUpfront, currency)}
                    </span>
                  }
                />
              </div>
              <div className="border-t pt-3">
                <InfoRow
                  label="Financed Principal"
                  value={formatCurrency(app.financedPrincipal, currency)}
                />
                {hasMarkup(app.markupAmount) && (
                  <InfoRow
                    label="Murabaha Markup"
                    value={formatCurrency(app.markupAmount, currency)}
                  />
                )}
                <InfoRow
                  label="Financed Amount"
                  value={
                    <span className="font-bold">
                      {formatCurrency(app.financedAmount, currency)}
                    </span>
                  }
                />
              </div>
              <div className="border-t pt-3">
                <InfoRow
                  label={schedule?.label ?? 'Installment'}
                  value={
                    <span className="text-lg font-bold">
                      {schedule
                        ? formatCurrency(schedule.amount, currency)
                        : '—'}
                    </span>
                  }
                />
                {schedule && (
                  <InfoRow label="Installments" value={schedule.count} />
                )}
                <InfoRow label="Term" value={`${app.termMonths} months`} />
                <InfoRow
                  label="Total Cost"
                  value={
                    <span className="font-bold">
                      {formatCurrency(
                        app.totalUpfront + app.financedAmount,
                        currency
                      )}
                    </span>
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Application Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow
                label="Application ID"
                value={<span className="font-mono text-xs">{app.id}</span>}
              />
              <InfoRow
                label="Created"
                value={formatDateTime(app.createdAt)}
              />
              <InfoRow
                label="Last Updated"
                value={formatDateTime(app.lastModifiedAt)}
              />
              {app.reviewedAt && (
                <InfoRow
                  label="Reviewed At"
                  value={formatDateTime(app.reviewedAt)}
                />
              )}
              {app.notes && <InfoRow label="Notes" value={app.notes} />}
            </CardContent>
          </Card>

          {/* Payments */}
          {payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Payments
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {payments.map((p) => (
                    <PaymentRow key={p.id} payment={p} currency={currency} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentRow({
  payment,
  currency,
}: {
  payment: ApplicationPaymentT;
  currency: string;
}) {
  const statusBadge: Record<string, { label: string; className: string }> = {
    completed: {
      label: 'Completed',
      className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    },
    pending: {
      label: 'Pending',
      className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-500/15 text-red-600 dark:text-red-400',
    },
    reversed: {
      label: 'Reversed',
      className: 'bg-gray-500/15 text-gray-600 dark:text-gray-400',
    },
  };
  const cfg = statusBadge[payment.status] ?? statusBadge.pending;
  const typeLabel = payment.type === 'down_payment' ? 'Deposit' : 'Excess';
  const when = payment.paidAt ?? payment.createdAt;

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{typeLabel}</span>
            <Badge className={cn('text-[10px] px-1.5 py-0', cfg.className)}>
              {cfg.label}
            </Badge>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {payment.initiator === 'agent' ? 'Pushed by agent' : 'Self-pay'}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {payment.payerMsisdn} · {payment.method.toUpperCase()} ·{' '}
            {formatDateTime(when)}
          </p>
          {payment.failureReason && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {payment.failureReason}
            </p>
          )}
        </div>
        <span className="text-sm font-bold shrink-0">
          {formatCurrency(payment.amount, currency)}
        </span>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
